import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { get } from '../../services/apiService';

const shortSha = (sha) => (sha ? sha.substring(0, 7) : '—');

const MethodMapsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const executionId = params.get('executionId');
    if (!executionId) {
      setError('Missing executionId in URL');
      setLoading(false);
      return;
    }
    get(`/api/recommended-tests/daily-regression/method-maps?executionId=${encodeURIComponent(executionId)}`)
      .then(res => { setData(res.data); setLoading(false); })
      .catch(err => { setError(err.message || 'Failed to load method maps'); setLoading(false); });
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Box p={4}><Alert severity="error">{error}</Alert></Box>;
  }

  const { currentCommit, baselineCommit, repo, branch, impactedMethods, recommendedTests } = data;

  const optimisedIds = new Set((recommendedTests || []).map(t => t.testDefinitionId).filter(Boolean));
  const optimisedPaths = new Set((recommendedTests || []).map(t => t.testPath).filter(Boolean));

  const testPathCount = new Map();
  (impactedMethods || []).forEach(entry => {
    (entry.tests || []).forEach(t => {
      const key = t.testPath || t.testName;
      if (key) testPathCount.set(key, (testPathCount.get(key) || 0) + 1);
    });
  });
  const duplicatePaths = new Set([...testPathCount.entries()].filter(([, c]) => c > 1).map(([k]) => k));

  const summaryRows = (impactedMethods || []).map(entry => {
    const simpleClass = (entry.className || '').split('/').pop() || entry.className;
    const total = (entry.tests || []).length;
    const selected = (entry.tests || []).filter(t =>
      t.testDefinitionId ? optimisedIds.has(t.testDefinitionId) : optimisedPaths.has(t.testPath || t.testName)
    ).length;
    return { simpleClass, methodName: entry.methodName, total, selected };
  });

  return (
    <Box sx={{ p: 4, maxWidth: 1100, mx: 'auto' }}>
      {/* Header */}
      <Typography variant="h5" fontWeight={700} mb={0.5}>
        Method Maps — {shortSha(currentCommit)}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {repo && branch ? `${repo} · ${branch} · ` : ''}
        {(impactedMethods || []).length} method{(impactedMethods || []).length !== 1 ? 's' : ''}
        {' · '}baseline {shortSha(baselineCommit)}
      </Typography>

      {/* Summary table */}
      <Typography variant="subtitle1" fontWeight={600} mb={1}>Summary</Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 600 }}>Class</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Method</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Total Tests</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Selected</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {summaryRows.map((row, i) => (
              <TableRow key={i} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'text.secondary' }}>
                  {row.simpleClass}
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 600 }}>
                  {row.methodName}
                </TableCell>
                <TableCell align="center">{row.total}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={row.selected}
                    size="small"
                    color={row.selected > 0 ? 'success' : 'default'}
                    variant={row.selected > 0 ? 'filled' : 'outlined'}
                    sx={{ fontSize: '0.75rem', height: 22, minWidth: 32 }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail section */}
      <Typography variant="subtitle1" fontWeight={600} mb={1}>Detail</Typography>
      <Box display="flex" flexDirection="column" gap={1.5}>
        {(impactedMethods || []).map((entry, i) => {
          const simpleClass = (entry.className || '').split('/').pop() || entry.className;
          return (
            <Paper key={i} variant="outlined">
              <Box sx={{ bgcolor: 'grey.100', px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', mr: 0.5 }}>
                    {simpleClass}.
                  </Typography>
                  {entry.methodName}
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    — {entry.tests?.length || 0} test{entry.tests?.length !== 1 ? 's' : ''}
                  </Typography>
                </Typography>
              </Box>
              <Box>
                {(entry.tests || []).map((test, j) => {
                  const isOptimised = test.testDefinitionId
                    ? optimisedIds.has(test.testDefinitionId)
                    : optimisedPaths.has(test.testPath || test.testName);
                  const isDuplicate = duplicatePaths.has(test.testPath || test.testName);
                  return (
                    <Box
                      key={j}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 0.75,
                        borderBottom: j < (entry.tests.length - 1) ? '1px solid' : 'none',
                        borderColor: 'divider'
                      }}
                    >
                      <Box>
                        <Typography variant="body2">
                          {test.testName || test.testPath || '(unnamed)'}
                        </Typography>
                        {test.testPath && test.testPath !== test.testName && (
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                            {test.testPath}
                          </Typography>
                        )}
                      </Box>
                      {(isOptimised || isDuplicate) && (
                        <Box display="flex" gap={0.5} sx={{ ml: 2, flexShrink: 0, mt: 0.25 }}>
                          {isOptimised && (
                            <Chip label="Selected" size="small" color="success" sx={{ fontSize: '0.68rem', height: 20 }} />
                          )}
                          {isDuplicate && (
                            <Chip label="Duplicate" size="small" color="warning" sx={{ fontSize: '0.68rem', height: 20 }} />
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

export default MethodMapsPage;
